"use client";

import { useState, useEffect, useRef, useCallback } from "react";

type Loc = { en: string; sl: string };

const COLS: { src: "goal" | "task" | "cal"; name: Loc; sub: Loc }[] = [
  {
    src: "goal",
    name: { en: "Goals", sl: "Cilji" },
    sub: { en: "what matters long-term", sl: "kar šteje dolgoročno" },
  },
  {
    src: "task",
    name: { en: "Tasks", sl: "Opravila" },
    sub: { en: "open & carried over", sl: "odprta in prenesena" },
  },
  {
    src: "cal",
    name: { en: "Calendar", sl: "Koledar" },
    sub: { en: "today's events", sl: "današnji dogodki" },
  },
];

const ITEMS: {
  src: "goal" | "task" | "cal";
  col: number;
  chip: Loc;
  plan: Loc;
  tag: Loc;
  carry?: Loc;
}[] = [
  {
    src: "goal",
    col: 0,
    chip: { en: "Strategy doc", sl: "Strateški dokument" },
    plan: { en: "Draft the strategy doc", sl: "Osnutek strateškega dokumenta" },
    tag: { en: "Goal", sl: "Cilj" },
  },
  {
    src: "task",
    col: 1,
    chip: { en: "Login bug", sl: "Napaka pri prijavi" },
    plan: { en: "Fix the login bug", sl: "Popravi napako pri prijavi" },
    tag: { en: "Task", sl: "Opravilo" },
    carry: { en: "carried over", sl: "preneseno" },
  },
  {
    src: "task",
    col: 1,
    chip: { en: "Investor reply", sl: "Odgovor investitorju" },
    plan: { en: "Reply to the investor", sl: "Odgovori investitorju" },
    tag: { en: "Task", sl: "Opravilo" },
  },
  {
    src: "cal",
    col: 2,
    chip: { en: "Team sync", sl: "Uskladitev z ekipo" },
    plan: { en: "Team sync", sl: "Uskladitev z ekipo" },
    tag: { en: "Calendar", sl: "Koledar" },
  },
  {
    src: "cal",
    col: 2,
    chip: { en: "Dentist", sl: "Zobozdravnik" },
    plan: { en: "Dentist appointment", sl: "Pregled pri zobozdravniku" },
    tag: { en: "Calendar", sl: "Koledar" },
  },
  {
    src: "goal",
    col: 0,
    chip: { en: "Evening run", sl: "Večerni tek" },
    plan: { en: "Run along the river", sl: "Tek ob reki" },
    tag: { en: "Goal", sl: "Cilj" },
  },
];

const PLAN = {
  cap: { en: "Mon · June 8", sl: "Pon · 8. jun" },
  ai: { en: "AI arranging", sl: "AI ureja" },
  title: { en: "Today's plan", sl: "Današnji načrt" },
  empty: {
    en: "Goals, tasks & calendar merge into one clear day.",
    sl: "Cilji, opravila in koledar se združijo v en jasen dan.",
  },
  foot: {
    en: "6 things that matter today — done at your own pace.",
    sl: "6 stvari, ki danes štejejo — opravljene v tvojem ritmu.",
  },
  replay: { en: "Replay", sl: "Ponovi" },
};

export function RealityDemo({ lang }: { lang: "en" | "sl" }) {
  const en = lang === "en";

  const [lit, setLit] = useState<number>(-1);
  const [spent, setSpent] = useState<number[]>([]);
  const [landed, setLanded] = useState<number[]>([]);
  const [done, setDone] = useState<boolean>(false);

  const stageRef = useRef<HTMLDivElement | null>(null);
  const dropRef = useRef<HTMLSpanElement | null>(null);
  const chipRefs = useRef<(HTMLDivElement | null)[]>([]);
  const dotRefs = useRef<(HTMLSpanElement | null)[]>([]);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);

  const clearTimers = useCallback(() => {
    timers.current.forEach((t) => clearTimeout(t));
    timers.current = [];
  }, []);

  const after = useCallback((ms: number, fn: () => void) => {
    timers.current.push(setTimeout(fn, ms));
  }, []);

  const fly = useCallback((i: number) => {
    const stage = stageRef.current;
    const chip = chipRefs.current[i];
    const drop = dropRef.current;
    const dot = dotRefs.current[i];
    if (!stage || !chip || !drop || !dot) return;
    const sb = stage.getBoundingClientRect();
    const cb = chip.getBoundingClientRect();
    const db = drop.getBoundingClientRect();
    const x0 = cb.left + cb.width / 2 - sb.left;
    const y0 = cb.top + cb.height / 2 - sb.top;
    const x1 = db.left + db.width / 2 - sb.left;
    const y1 = db.top + db.height / 2 - sb.top;
    dot.style.setProperty("--x0", `${x0}px`);
    dot.style.setProperty("--y0", `${y0}px`);
    dot.style.setProperty("--x1", `${x1}px`);
    dot.style.setProperty("--y1", `${y1}px`);
    dot.classList.remove("go");
    void dot.offsetWidth;
    dot.classList.add("go");
  }, []);

  const run = useCallback(() => {
    clearTimers();
    setLit(-1);
    setSpent([]);
    setLanded([]);
    setDone(false);

    const prefersReduced =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced) {
      setLanded(ITEMS.map((_, i) => i));
      setDone(true);
      return;
    }

    const START = 1050;
    const GAP = 750;
    const TRAVEL = 620;

    ITEMS.forEach((_, i) => {
      after(START + i * GAP, () => {
        setLit(i);
        fly(i);
      });
      after(START + i * GAP + TRAVEL, () => {
        setLit((cur) => (cur === i ? -1 : cur));
        setSpent((s) => [...s, i]);
        setLanded((l) => [...l, i]);
      });
    });

    const END = START + 6 * GAP + TRAVEL;
    after(END + 120, () => setDone(true));
    after(END + 4400, () => run());
  }, [after, clearTimers, fly]);

  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;
    let started = false;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !started) {
            started = true;
            run();
          }
        });
      },
      { threshold: 0.28 }
    );
    obs.observe(stage);
    return () => {
      obs.disconnect();
      clearTimers();
    };
  }, [run, clearTimers]);

  return (
    <>
      {/* Scroll-pause marker — <ScrollPauseController> hard-pins here so a fast
          fling stops ON the demo (the goals→tasks→calendar → plan animation),
          not up at the section heading. `data-pause-offset` (a touch above the
          72px header) leaves a sliver of empty space above the demo while the
          subhead text stays tucked up under the sticky header — not visible. */}
      <div className="scroll-stop h-px" aria-hidden data-pause-offset="90" />
      <div className="stage" ref={stageRef}>
      <div className="sources">
        {COLS.map((col) => {
          const colChips = ITEMS.map((it, i) => ({ it, i })).filter(
            ({ it }) => it.src === col.src
          );
          return (
            <div className={`col ${col.src}`} key={col.src}>
              <div className="col-head">
                <span className="col-dot" />
                <span className="col-name">{en ? col.name.en : col.name.sl}</span>
                <span className="col-sub">{en ? col.sub.en : col.sub.sl}</span>
              </div>
              <div className="chips">
                {colChips.map(({ it, i }) => (
                  <div
                    key={i}
                    ref={(el) => {
                      chipRefs.current[i] = el;
                    }}
                    className={`chip${lit === i ? " lit" : ""}${
                      spent.includes(i) ? " spent" : ""
                    }`}
                  >
                    <div className="ct">{en ? it.chip.en : it.chip.sl}</div>
                    {it.carry && (
                      <div className="cmeta carry">
                        {en ? it.carry.en : it.carry.sl}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <div className="funnel funnel-mobile">
        <svg viewBox="0 0 100 58" preserveAspectRatio="none">
          <path className="fn-line" d="M16.7 0 C16.7 34, 50 22, 50 58" />
          <path className="fn-line" d="M50 0 L50 58" />
          <path className="fn-line" d="M83.3 0 C83.3 34, 50 22, 50 58" />
        </svg>
      </div>
      <div className="funnel funnel-desktop">
        <svg viewBox="0 0 100 100" preserveAspectRatio="none">
          <path className="fn-line" d="M2 16.7 C 46 16.7, 54 50, 96 50" />
          <path className="fn-line" d="M2 50 L96 50" />
          <path className="fn-line" d="M2 83.3 C 46 83.3, 54 50, 96 50" />
        </svg>
      </div>

      <div className="plan">
        <span ref={dropRef} className="dropmark" />
        <div className="plan-cap">
          <span>{en ? PLAN.cap.en : PLAN.cap.sl}</span>
          <span className={`ai${lit >= 0 || done ? " show" : ""}`}>
            <span className="pulse" />
            {en ? PLAN.ai.en : PLAN.ai.sl}
          </span>
        </div>
        <div className="plan-title">{en ? PLAN.title.en : PLAN.title.sl}</div>
        <div className="plan-list">
          <div className="plan-empty" style={{ opacity: landed.length ? 0 : 1 }}>
            {en ? PLAN.empty.en : PLAN.empty.sl}
          </div>
          {landed.map((i) => {
            const it = ITEMS[i];
            return (
              <div className={`prow in ${it.src}`} key={i}>
                <span className="nm">
                  {en ? it.plan.en : it.plan.sl}
                  {it.carry && <small>{en ? it.carry.en : it.carry.sl}</small>}
                </span>
                <span className={`src ${it.src}`}>
                  {en ? it.tag.en : it.tag.sl}
                </span>
              </div>
            );
          })}
        </div>
        <div className={`plan-foot${done ? " show" : ""}`}>
          <span className="tick" />
          <span>{en ? PLAN.foot.en : PLAN.foot.sl}</span>
        </div>
      </div>

      {ITEMS.map((it, i) => (
        <span
          key={i}
          ref={(el) => {
            dotRefs.current[i] = el;
          }}
          className={`flier ${it.src}`}
          onAnimationEnd={(e) => {
            (e.currentTarget as HTMLSpanElement).classList.remove("go");
          }}
        />
      ))}
      </div>
    </>
  );
}
